import React, { createRef, useCallback, useEffect, useState } from 'react';
import { IframeComponent } from '@src/interfaces';
import { trackEvent, trackPage } from '@src/utils/et';

const calcHeight = (
  { width, height, ratio: modelRatio }: Pick<IframeComponent, 'width' | 'height' | 'ratio'>,
  ref: HTMLDivElement,
) => {
  function getWidthNumber(): number {
    if (width && Number.isSafeInteger(Number(width))) {
      return Number(width);
    }

    const pxMatch = width?.match(/^(\d)px?$/);
    if (pxMatch && pxMatch.length === 2) {
      return Number(pxMatch[1]);
    }

    const percMatch = width && width.match(/^(\d)%?$/);
    const perc = percMatch && percMatch.length === 2 ? Number(percMatch[1]) : 100;

    return Math.floor((ref.offsetWidth * perc) / 100);
  }

  if (height) {
    return height;
  }

  const widthNumber = getWidthNumber();
  const [x, y] = modelRatio.split(':').map(_x => (_x === 'phi' ? 1.618 : _x));
  const realRatio = Number(y) / Number(x);
  return Math.ceil(widthNumber * realRatio);
};

const Iframe = (props: IframeComponent) => {
  const iframeRef = createRef<HTMLIFrameElement>()
  const width = props.width ?? '100%';
  const [height, setHeight] = useState<string | number>(0);

  // Handle messages coming from child page.
  const handleMessage = useCallback((event: MessageEvent) => {
    const iframeEl = iframeRef.current

    if (!iframeEl) {
      console.error('HandleMessage error: iframe not found')
      return;
    }
    const { data } = event;

    // Ensure the message comes from a trusted source.
    const iframeOrigin = new URL(props.url).origin;
    const {iframeSrc} = data
    if (event.origin !== iframeOrigin || iframeSrc && iframeSrc !== props.url) {
      // If event comes from a wrong src.
      return;
    }
    console.log('Message received:', data);

    if (data && data.cmd) {
      switch (data.cmd) {
        case 'resize':
          console.log('Resize requested. New height:', data.height);
          iframeEl.style.height = `${data.height }px`;
          break;
        case 'scrollToTop':
          console.log('Scroll to top of iframe requested.');
          iframeEl.scrollIntoView({ behavior: 'smooth' });
          break;
        case 'trackPage':
          console.log('Tracking page:', data.context);
          trackPage(data.context);
          break;
        case 'trackEvent':
          console.log('Tracking event:', data.eventName, data.payload);
          trackEvent(data.eventName, data.payload)
          break;
        default:
          console.log(`Message of type ${data.cmd} is not handled`)
          break;
      }
    }
  }, [iframeRef, props.url])

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage)
  }, [handleMessage]);

  const ref = useCallback(
    (node: HTMLDivElement) => {
      if (node !== null) {
        const newHeight = calcHeight(
          {
            width: props.width,
            height: props.height,
            ratio: props.ratio,
          },
          node,
        );
        setHeight(newHeight);
      }
    },
    [props.width, props.height, props.ratio],
  );

  return (
    <section ref={ref} className="paragraph iframe-paragraph" id={props.applyInlineCss ? props.wrapperId : undefined}>
      {props.title ? <h3>{props.title}</h3> : null}

      {/* eslint-disable-next-line react/no-danger */}
      {props.applyInlineCss && !!props.inlineCss && <style dangerouslySetInnerHTML={{__html: props.inlineCss }}/>}

      <iframe
        ref={iframeRef}
        allowFullScreen
        frameBorder="0"
        height={height}
        src={props.url}
        title="Iframe"
        width={width}
      />
    </section>
  );
};

export default Iframe;
